import { Box, Paper, Stack, Typography, useTheme, alpha } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CircleIcon from "@mui/icons-material/Circle";
import { useTenantTranslation } from "hooks/formatters";
import { useMaterials } from "hooks/useMaterials";
import { StrikeRule } from "src/store/api/generatedApi";
import { STRIKE_COLORS } from "components/common/plots/colorSets";

type StrikeRulesBoxProps = Readonly<{
  strikeRules: StrikeRule[];
}>;

export const StrikeRulesBox = ({ strikeRules }: StrikeRulesBoxProps) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const { materials: materialMetadata } = useMaterials();

  if (strikeRules.length === 0) {
    return null;
  }

  const orderingByName = new Map(
    (materialMetadata ?? [])
      .slice()
      .sort((a, b) => a.ordering - b.ordering)
      .map((m) => [m.name, m.ordering] as const)
  );
  const sortedStrikeRules = [...strikeRules].sort((a, b) => {
    const aOrder =
      orderingByName.get(a.material_name) ?? Number.MAX_SAFE_INTEGER;
    const bOrder =
      orderingByName.get(b.material_name) ?? Number.MAX_SAFE_INTEGER;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return a.material_name.localeCompare(b.material_name);
  });

  const ruleLines = sortedStrikeRules.flatMap((rule) => [
    ...(rule.strike_if_below !== null
      ? [
          {
            key: `${rule.material_name}-below`,
            text: t("fewerThanNLiftsOf", {
              count: rule.strike_if_below,
              material: rule.material_name,
            }),
          },
        ]
      : []),
    ...(rule.strike_if_above !== null
      ? [
          {
            key: `${rule.material_name}-above`,
            text: t("moreThanNLiftsOf", {
              count: rule.strike_if_above,
              material: rule.material_name,
            }),
          },
        ]
      : []),
    ...(rule.ratio_tolerance != null && rule.normalisation_material_name != null
      ? [
          {
            key: `${rule.material_name}-ratio`,
            text: t("recipeRatioToleranceRule", {
              material: rule.material_name,
              normalisationMaterial: rule.normalisation_material_name,
              tolerance: rule.ratio_tolerance,
            }),
          },
        ]
      : []),
  ]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        bgcolor: alpha(theme.palette.info.main, 0.04),
        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
        borderRadius: 2,
      }}
    >
      <Stack
        spacing={1.5}
        justifyContent="space-between"
        sx={{ height: "100%" }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <InfoOutlinedIcon
            sx={{ fontSize: 18, color: theme.palette.info.main }}
          />
          <Typography
            variant="body2"
            sx={{
              fontWeight: "fontWeightSemiBold",
              color: theme.palette.text.primary,
            }}
          >
            {t("strikeRulesPerHeat")}
          </Typography>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {t("strikeRulesCurrentNote")}
        </Typography>

        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {ruleLines.map((line) => (
            <Box
              component="li"
              key={line.key}
              sx={{
                color: theme.palette.text.secondary,
                py: 0.25,
              }}
            >
              <Typography variant="body2" component="span">
                {line.text}
              </Typography>
            </Box>
          ))}
        </Box>

        <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 0.5 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <CircleIcon sx={{ fontSize: 10, color: STRIKE_COLORS.green }} />
            <Typography variant="body2" color="text.secondary">
              {`0 ${t("strikes")} = ${t("green")}`}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <CircleIcon sx={{ fontSize: 10, color: STRIKE_COLORS.amber }} />
            <Typography variant="body2" color="text.secondary">
              {`1 ${t("strike")} = ${t("amber")}`}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <CircleIcon sx={{ fontSize: 10, color: STRIKE_COLORS.red }} />
            <Typography variant="body2" color="text.secondary">
              {`2+ ${t("strikes")} = ${t("red")}`}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
};
